import {OnChangeFn} from "@tanstack/react-table";
import {ReactNode, useCallback, useMemo} from "react";
import {useNavigate} from "react-router";

import {AssistantEmptyStateCta} from "@/components/assistant/AssistantEmptyStateCta.tsx";
import {useCurrentUserContext} from "@/components/context/hooks.ts";
import {SelectOption} from "@/components/select/Select.types.ts";
import {EmptyTable} from "@/components/table/subComponents/EmptyTable/EmptyTable.tsx";
import {LimitAndOffset} from "@/components/table/subComponents/Paginator/Paginator.tsx";
import {Table} from "@/components/table/Table.tsx";
import {ColumnSpec, Sorting, SortingDirection} from "@/components/table/Table.types.ts";
import {AnalyzerSource} from "@/lib/queries/analyzerRuns.ts";
import {Service} from "@/lib/types/checks.ts";
import {buildColumnSpecs, DEFAULT_COLUMNS} from "@/pages/checks/list/columns/checksColumnSpecs.tsx";
import {useChecksListViews} from "@/pages/checks/list/columns/useChecksListViews.ts";
import {getCheckDetailUrl, isEmptyGroupCheck} from "@/pages/checks/utils.ts";

type ChecksTableProps = {
  actionBar: ReactNode;
  items: Service[];
  sortColumn: string;
  sortDirection: SortingDirection;

  rowSelection: Record<string, boolean>;
  setRowSelection: OnChangeFn<Record<string, boolean>>;

  onSortingChange?: (sorting: Sorting) => void;

  pagination?: LimitAndOffset;
  totalRows?: number;
  updatePagination?: (laf: LimitAndOffset) => void;
  paginationOptions?: SelectOption<number>[];
  paginationData?: unknown[];
  isFiltered?: boolean;
};

export const ChecksTable = ({
  actionBar,
  items,
  sortColumn,
  sortDirection,
  onSortingChange,
  rowSelection,
  setRowSelection,
  pagination,
  totalRows,
  updatePagination,
  paginationOptions,
  paginationData,
  isFiltered,
}: ChecksTableProps) => {
  const {user} = useCurrentUserContext();
  const {isEnabled: viewsEnabled, activeView} = useChecksListViews();
  const navigate = useNavigate();

  const onRowClick = useCallback(
    (service: Service) => {
      navigate(getCheckDetailUrl(service.id, service.monitoring_service_type, isEmptyGroupCheck(service)));
    },
    [navigate]
  );

  const activeViewColumns = activeView?.columns;
  const specs: ColumnSpec<Service>[] = useMemo(() => {
    if (!user) return [];
    if (viewsEnabled && activeViewColumns) {
      return buildColumnSpecs(activeViewColumns, user, onRowClick);
    }
    return buildColumnSpecs(DEFAULT_COLUMNS, user, onRowClick);
  }, [viewsEnabled, activeViewColumns, user, onRowClick]);

  return (
    <Table
      actionBar={actionBar}
      rows={items}
      minWidth="800px"
      initialState={{columnPinning: {right: ["quick_actions"]}}}
      sorting={{column: sortColumn, direction: sortDirection}}
      onSortingChange={onSortingChange}
      rowSelection={rowSelection}
      setRowSelection={setRowSelection}
      tableRowClassName="cursor-pointer"
      noDataComponent={
        isFiltered ? (
          <EmptyTable>No Checks found matching the filter(s) above.</EmptyTable>
        ) : (
          <AssistantEmptyStateCta
            title="You do not have any Checks"
            assistantTitle="Set up your first Checks"
            source={AnalyzerSource.CHECKS_LIST_EMPTY_TABLE}
          />
        )
      }
      specs={specs}
      pagination={pagination}
      totalRows={totalRows}
      updatePagination={updatePagination}
      paginationOptions={paginationOptions}
      paginationData={paginationData}
    />
  );
};
